import React from 'react';
import { Input, Select, H2, Contact, InternationalContact, Icon } from '@tviklax/ui-components';
import { Section, FormGrid, FormGroup, AddressCard, Label } from './styles';
import { OrderFormData } from './types';
import { AddressAutocomplete, AddressAutofillFields } from './AddressAutocomplete';
import { AccountAutocomplete, AccountOption } from './AccountAutocomplete';
import { useCountries, useStatesForCountry } from './addressData';

interface VendorInformationProps {
  formData: OrderFormData;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void;
  onEmailChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onPhoneChange: (value: string) => void;
  onEmailValidate?: (isValid: boolean) => void;
  onPhoneValidate?: (isValid: boolean) => void;
  onAddressAutofill: (fields: Partial<OrderFormData>) => void;
}

export const VendorInformation: React.FC<VendorInformationProps> = ({
  formData,
  onChange,
  onEmailChange,
  onPhoneChange,
  onEmailValidate,
  onPhoneValidate,
  onAddressAutofill,
}) => {
  const countries = useCountries();
  const stateOptions = useStatesForCountry(formData.vendorCountry);

  const handleVendorAddressSelected = (fields: AddressAutofillFields) => {
    onAddressAutofill({
      vendorAddr1: fields.street,
      vendorCity: fields.city,
      vendorState: fields.state,
      vendorZip: fields.postalCode,
      vendorCountry: fields.country,
    });
  };

  const handleVendorAccountSelected = (account: AccountOption) => {
    onAddressAutofill({
      VendorName: account.name,
      VendorEmail: account.email || formData.VendorEmail,
      VendorContactNumber: account.phone || formData.VendorContactNumber,
      vendorCountry: account.country || formData.vendorCountry,
    });
  };

  return (
    <Section>
      <H2 icon={<Icon name="Building2" size={20} color="#f86400" />}>Vendor Information</H2>
      <FormGrid style={{ marginBottom: '24px' }}>
        <FormGroup $span={6}>
          <AccountAutocomplete
            accountType="vendor"
            label="Vendor Name"
            name="VendorName"
            value={formData.VendorName}
            onChange={onChange}
            onAccountSelected={handleVendorAccountSelected}
            placeholder="Enter vendor legal name"
            required
          />
        </FormGroup>
        <FormGroup $span={3}>
          <Contact
            variant="email"
            value={formData.VendorEmail}
            onChange={onEmailChange}
            onValidate={onEmailValidate}
            label="Email Address"
            required
          />
        </FormGroup>
        <FormGroup $span={3}>
          <InternationalContact
            variant="cell"
            value={formData.VendorContactNumber}
            onChange={onPhoneChange}
            onValidate={onPhoneValidate}
            defaultCountry={formData.vendorCountry || 'IN'}
            label="Contact Number"
            required
          />
        </FormGroup>
      </FormGrid>

      <Label style={{ marginBottom: '8px', display: 'block' }}>Vendor Address</Label>
      <AddressCard>
        <FormGroup $span={6}>
          <AddressAutocomplete
            label="Street Address"
            name="vendorAddr1"
            value={formData.vendorAddr1}
            onChange={onChange}
            onAddressSelected={handleVendorAddressSelected}
            placeholder="e.g., 456 Industrial Blvd"
            required
          />
        </FormGroup>
        <FormGroup $span={3}>
          <Input
            label="Apt / Suite"
            name="vendorAddr2"
            value={formData.vendorAddr2}
            onChange={onChange}
            placeholder="Unit B"
          />
        </FormGroup>
        <FormGroup $span={3}>
          <Input
            label="Zip Code"
            name="vendorZip"
            value={formData.vendorZip}
            onChange={onChange}
            placeholder="Zip Code"
            required
          />
        </FormGroup>
        <FormGroup $span={2}>
          <Input
            label="City"
            name="vendorCity"
            value={formData.vendorCity}
            onChange={onChange}
            placeholder="City"
            required
          />
        </FormGroup>
        <FormGroup $span={2}>
          <Select
            label="State"
            name="vendorState"
            value={formData.vendorState}
            onChange={onChange}
            items={stateOptions}
            placeholder="Select State"
            disabled={stateOptions.length === 0}
            required={stateOptions.length > 0}
          />
        </FormGroup>
        <FormGroup $span={2}>
          <Select
            label="Country"
            name="vendorCountry"
            value={formData.vendorCountry}
            onChange={onChange}
            items={countries}
            placeholder="Select Country"
            required
          />
        </FormGroup>
      </AddressCard>
    </Section>
  );
};
